import { Phone, Clock, Shield, Calendar, Mail } from "lucide-react";
import { motion } from "framer-motion";
import { contactInfo } from "../data";
import CoverageMap from "../components/CoverageMap";

const fadeIn = {
  initial: { opacity: 0, y: 15 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function Contact() {
  return (
    <div className="bg-brand-cream-light min-h-screen overflow-hidden">
      {/* Header Section with Background Image */}
      <section className="relative pt-32 pb-16 lg:pt-48 lg:pb-24 flex items-center justify-center text-center">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/hero-footcare.png"
            alt="Kontakt Juhler Fodpleje"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"></div>
        </div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 mx-auto max-w-4xl px-6 lg:px-8"
        >
          <span className="text-[#fdedd3] font-semibold text-xs uppercase tracking-widest bg-white/10 backdrop-blur-md px-3 py-1 rounded-full border border-white/10">
            Tidsbestilling & Spørgsmål
          </span>
          <h1 className="text-4xl font-light tracking-tight text-white sm:text-6xl mt-4 font-display">
            Kontakt Juhler Fodpleje
          </h1>
          <p className="mt-6 text-lg text-stone-200 max-w-xl mx-auto leading-relaxed">
            Jeg glæder mig til at byde dig velkommen. Ring eller send en SMS for at bestille tid i en af mine faste klinikker eller til et udekørende hjemmebesøg i Kruså/Padborg & omegn samt Faaborg/Millinge & omegn.
          </p>
        </motion.div>
      </section>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 pb-16 lg:pb-24 pt-16 lg:pt-24">
        {/* Streamlined Contact Card with Image Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch mb-16 lg:mb-24">
          {/* Left: Contact Card */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-[2rem] border border-stone-100 shadow-xs p-8 lg:p-10 lg:col-span-7 flex flex-col justify-between"
          >
            {/* Primary Action: Phone Booking */}
            <div className="text-center max-w-xl mx-auto pb-10 border-b border-stone-100 w-full">
              <div className="inline-flex h-12 w-12 rounded-full bg-brand-light text-brand-medium items-center justify-center mb-4">
                <Phone className="h-5 w-5" />
              </div>
              <p className="text-xs font-bold text-brand-medium uppercase tracking-widest mb-1">Telefonisk Booking</p>
              <a 
                href={`tel:${contactInfo.phone}`} 
                className="text-3xl sm:text-4xl font-light text-stone-900 hover:text-brand-medium transition-colors block py-2 font-display tracking-tight"
              >
                {contactInfo.phoneDisplay}
              </a>
              <p className="text-stone-600 text-sm mt-1">
                Ring eller send en SMS for tidsbestilling.
              </p>
              <div className="mt-4 bg-brand-cream/40 border border-brand-cream px-5 py-3.5 rounded-2xl max-w-lg mx-auto text-left flex items-start gap-3">
                <i className="fa-solid fa-circle-info text-brand-medium mt-0.5 text-base shrink-0" aria-hidden="true" />
                <p className="text-stone-700 text-sm leading-relaxed">
                  <strong>Bemærk:</strong> Hvis jeg ikke tager telefonen, er jeg i gang med en behandling. Læg gerne en besked eller send en SMS, så ringer jeg tilbage hurtigst muligt.
                </p>
              </div>
            </div>

            {/* Clinic Addresses Card */}
            <div className="py-8 border-b border-stone-100">
              <h3 className="text-sm font-bold text-stone-900 uppercase tracking-wider mb-4 text-center">Fysiske Klinikker & Udekørende</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-stone-50 p-4 rounded-xl border border-stone-200/60 flex items-start gap-3">
                  <div className="p-2 bg-brand-light/60 rounded-lg text-brand-medium shrink-0 mt-0.5">
                    <i className="fa-solid fa-house-chimney-medical" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-stone-900">Kollund Klinik</h4>
                    <p className="text-xs text-stone-600 mt-0.5">Fjordvænget 9, Kollund</p>
                    <p className="text-xs text-stone-600">6340 Kruså (Kruså / Padborg & omegn)</p>
                  </div>
                </div>
                <div className="bg-stone-50 p-4 rounded-xl border border-stone-200/60 flex items-start gap-3">
                  <div className="p-2 bg-brand-light/60 rounded-lg text-brand-medium shrink-0 mt-0.5">
                    <i className="fa-solid fa-house-chimney-medical" />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-stone-900">Dyreborg Klinik</h4>
                    <p className="text-xs text-stone-600 mt-0.5">Knoldsborgvej 6, Dyreborg</p>
                    <p className="text-xs text-stone-600">5600 Faaborg (Faaborg / Millinge & omegn)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Practical Info Grid (3 Columns) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-10">
              {/* Opening Hours */}
              <div className="flex flex-col items-center text-center px-2">
                <div className="h-10 w-10 rounded-xl bg-brand-cream text-brand-medium flex items-center justify-center mb-3">
                  <Clock className="h-5 w-5" />
                </div>
                <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-1">Åbningstider</h3>
                <p className="text-base font-medium text-stone-800 mb-1">{contactInfo.hours}</p>
                <p className="text-stone-500 text-xs">
                  Behandling foregår efter aftale i klinik eller som hjemmebesøg.
                </p>
              </div>

              {/* Consent & Safety */}
              <div className="flex flex-col items-center text-center px-2 border-t md:border-t-0 md:border-x border-stone-100 pt-6 md:pt-0">
                <div className="h-10 w-10 rounded-xl bg-brand-cream text-brand-medium flex items-center justify-center mb-3">
                  <Shield className="h-5 w-5" />
                </div>
                <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-1">Samtykke & Sikkerhed</h3>
                <p className="text-stone-800 text-xs leading-relaxed max-w-[240px]">
                  Alle kunder underskriver en samtykkeerklæring før første behandling. Opbevares sikkert og fortroligt.
                </p>
              </div>

              {/* Cancellation */}
              <div className="flex flex-col items-center text-center px-2 border-t md:border-t-0 pt-6 md:pt-0">
                <div className="h-10 w-10 rounded-xl bg-brand-cream text-brand-medium flex items-center justify-center mb-3">
                  <Calendar className="h-5 w-5" />
                </div>
                <h3 className="text-sm font-semibold text-stone-900 uppercase tracking-wider mb-1">Afbud & Udeblivelse</h3>
                <p className="text-stone-800 text-xs leading-relaxed max-w-[240px]">
                  Afbud senest 24 timer før. Ved udeblivelse uden afbud opkræves et gebyr på <strong className="font-semibold text-stone-900">250 kr.</strong>
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right: Beautiful Image */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-5 relative min-h-[350px] lg:min-h-full rounded-[2rem] overflow-hidden shadow-xs border border-stone-100 bg-white p-3 flex"
          >
            <img 
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/assets/aec437a7-dbd3-431a-ae4e-b42ebba93fa1/69589887-0f7f-4aab-9013-7a11d7617662.png" 
              alt="Pernille udfører professionel fodbehandling i hyggelige og trygge rammer" 
              className="w-full h-full object-cover rounded-[1.5rem]"
            />
            <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-xs p-4 rounded-xl border border-stone-100 shadow-xs">
              <p className="text-xs font-semibold text-stone-900">Professionel pleje i trygge rammer</p>
              <p className="text-[11px] text-stone-500 mt-0.5">Jeg medbringer alt nødvendigt udstyr til udekørende behandlinger.</p>
            </div>
          </motion.div>
        </div>

        {/* Interactive Coverage Map Section (Preserved as requested) */}
        <div className="mt-12 -mx-6 lg:-mx-8">
          <CoverageMap />
        </div>

        {/* Bottom Note */}
        <motion.div 
          {...fadeIn}
          className="mt-16 text-center max-w-2xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-stone-100 text-stone-600 text-sm font-medium mb-4 shadow-xs">
             <Mail className="h-4 w-4 text-brand-medium" />
             <span>{contactInfo.email}</span>
          </div>
          <p className="text-stone-500 text-xs px-4 leading-relaxed">
            Bemærk: Jeg har ikke et online bookingsystem, da den personlige kontakt er vigtig for at sikre den rette tid og behandling til dine behov.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
